import { Link } from 'react-router-dom'

interface ProjectCardProject {
  id: string
  title?: string
  description?: string
  image?: string
  status?: string
  technologies?: string[]
  features?: string[]
  duration?: string
  team?: string
  type?: 'business' | 'personal'
  category?: string
}

interface ProjectCardProps {
  project: ProjectCardProject
}

export default function ProjectCard({ project }: ProjectCardProps) {
  const looksLikeImageUrl = (value: unknown): value is string => {
    if (typeof value !== 'string') return false
    const str = value.trim()
    if (!str) return false
    if (str.startsWith('/') || str.startsWith('http://') || str.startsWith('https://') || str.startsWith('//')) return true
    if (/^img\.ionicerrrrscode\.com\//i.test(str)) return true
    return false
  }

  const normalizeImageUrl = (value: string) => {
    const str = value.trim()
    if (str.startsWith('//')) return `https:${str}`
    if (/^img\.ionicerrrrscode\.com\//i.test(str)) return `https://${str}`
    return str
  }

  const technologies = Array.isArray(project.technologies)
    ? project.technologies.filter((tech): tech is string => typeof tech === 'string')
    : []
  const features = Array.isArray(project.features)
    ? project.features.filter((feature): feature is string => typeof feature === 'string')
    : []
  const status = typeof project.status === 'string' && project.status ? project.status : 'In Progress'
  const duration = typeof project.duration === 'string' && project.duration ? project.duration : 'Timeline TBD'
  const team = typeof project.team === 'string' && project.team ? project.team : 'Team TBD'
  const title = typeof project.title === 'string' && project.title ? project.title : 'Untitled Project'
  const description = typeof project.description === 'string' ? project.description : ''
  
  const isWeb = 
    (typeof project.category === 'string' && project.category.toLowerCase().includes('web')) ||
    (typeof project.title === 'string' && (
      project.title.toLowerCase().includes('web') || 
      project.title.toLowerCase().includes('website')
    ))
  
  const fallbackIcon = isWeb ? '💻' : '📱'
  const image = typeof project.image === 'string' && project.image ? project.image : fallbackIcon

  return (
    <div className="card-studio flex flex-col justify-between p-6 min-h-[480px] h-full">
      <div>
        {/* Project Header */}
        <div className="flex items-start gap-4 mb-4">
          <div
            className="w-14 h-14 rounded-lg overflow-hidden flex flex-shrink-0 items-center justify-center border"
            style={{
              background: 'var(--surface-2)',
              borderColor: 'var(--border)',
            }}
          >
            {looksLikeImageUrl(image) ? (
              <img
                src={normalizeImageUrl(image)}
                alt={title}
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                  (e.target as HTMLImageElement).nextElementSibling!.classList.remove('hidden');
                }}
              />
            ) : (
              <span className="text-2xl">{image}</span>
            )}
            <div className="hidden w-full h-full flex items-center justify-center">
              <span className="text-2xl">{fallbackIcon}</span>
            </div>
          </div>
          <div>
            <h3 
              className="font-display text-[1.18rem] font-semibold mb-1"
              style={{ color: 'var(--text)', letterSpacing: '-0.01em' }}
            >
              {title}
            </h3>
            <span className="badge">
              <span className="badge-dot" style={{ background: status === 'Completed' ? 'var(--accent)' : '#eab308' }} />
              {status}
            </span>
          </div>
        </div>

        {/* Description */}
        <p 
          className="text-sm leading-relaxed mb-5" 
          style={{ 
            color: 'var(--muted)',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            height: '4.5em'
          }}
        >
          {description}
        </p>

        {/* Technologies */}
        {technologies.length > 0 && (
          <div className="mb-5">
            <h4 className="text-xs uppercase tracking-wider font-semibold mb-2" style={{ color: 'var(--muted)' }}>
              Technologies
            </h4>
            <div className="flex flex-wrap gap-1.5">
              {technologies.map((tech) => (
                <span key={tech} className="tag-chip">
                  {tech}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Key Features */}
        {features.length > 0 && (
          <div className="mb-5">
            <h4 className="text-xs uppercase tracking-wider font-semibold mb-2" style={{ color: 'var(--muted)' }}>
              Features
            </h4>
            <ul className="text-sm space-y-1.5 list-none p-0 m-0" style={{ color: 'var(--muted)' }}>
              {features.slice(0, 3).map((feature) => (
                <li key={feature} className="flex items-start gap-2">
                  <span style={{ color: 'var(--border-strong)' }}>◦</span>
                  <span>{feature}</span>
                </li>
              ))}
              {features.length > 3 && (
                <li className="text-xs font-mono" style={{ color: 'var(--muted)', opacity: 0.8 }}>
                  +{features.length - 3} more features
                </li>
              )}
            </ul>
          </div>
        )}
      </div>

      <div>
        {/* Project Details */}
        <div className="flex items-center gap-4 text-xs font-mono mb-4" style={{ color: 'var(--muted)' }}>
          <span>{duration}</span>
          <span>•</span>
          <span>{team}</span>
        </div>
        
        <Link to={project.type === 'personal' ? `/projects/personal/${project.id}` : `/projects/${project.id}`} className="no-underline inline-block">
          <span 
            className="text-sm font-semibold hover:underline" 
            style={{ color: 'var(--text)' }}
          >
            View Details →
          </span>
        </Link>
      </div>
    </div>
  )
}
